import { useI18n } from "vue-i18n";
import { toast } from "vue-sonner";

const SUCCESS_TOAST_ID = "table-refresh-success";
const ERROR_TOAST_ID = "table-refresh-error";

/** A refetch result of TanStack Query reports its failure in the value instead of rejecting. */
function isFailedResult(result: unknown): boolean {
  if (typeof result !== "object" || result === null) {
    return false;
  }
  const { isError, status } = result as { isError?: unknown, status?: unknown };
  return isError === true || status === "error";
}

/** A refresh that TanStack Query cancelled because a newer one started is not a failure. */
function isCancellation(error: unknown): boolean {
  return typeof error === "object" && error !== null && (error as { name?: unknown }).name === "CancelledError";
}

/**
 * Runs a refresh the person asked for (the refresh button, the retry button of an error state) and tells them how it
 * went: "Lijst vernieuwd" on success, "Vernieuwen mislukt." with a retry action on failure. Automatic refetches
 * (Mercure, polling, filter, sort, paging, mount) must not go through this, they stay silent.
 *
 * Only the outcome of the latest call is shown: a refresh that a newer click superseded or that was cancelled shows
 * nothing. The toasts have stable ids and the other outcome is dismissed first, so clicks never stack toasts.
 */
export function useRefreshToast() {
  const { t } = useI18n();
  let latestRun = 0;

  /** Returns true when the refresh succeeded, false when it failed, was cancelled or was superseded. */
  async function runRefresh(refresh: () => unknown | Promise<unknown>): Promise<boolean> {
    const run = ++latestRun;

    let failed = false;
    try {
      failed = isFailedResult(await refresh());
    } catch (error) {
      if (isCancellation(error)) {
        return false;
      }
      failed = true;
    }

    if (run !== latestRun) {
      return false;
    }

    toast.dismiss(failed ? SUCCESS_TOAST_ID : ERROR_TOAST_ID);

    if (failed) {
      toast.error(t("base.table.refresh_failed"), {
        id: ERROR_TOAST_ID,
        action: {
          label: t("base.table.refresh_retry"),
          onClick: () => {
            void runRefresh(refresh);
          },
        },
      });
      return false;
    }

    toast.success(t("base.table.refreshed"), { id: SUCCESS_TOAST_ID });
    return true;
  }

  return { runRefresh };
}
